import { Obelisk } from './exploration.interfaces';
import { QuestMetadata } from './quest-core.interfaces';
import { User } from './user.interfaces';
import { Pathway } from './map-markings.interfaces';
import { GeoCoords } from './geo-and-movement.interfaces';

export type CompilationType = 'compilation' | 'trip';

// Trip Editor related interfaces
export interface TripStats {
  totalLength?: number; // in meters
  totalTime?: number; // in seconds
  elevationGain?: number; // in meters
  elevationLoss?: number; // in meters
  pavedRoadsPercent?: number; // 0-100
  unpavedRoadsPercent?: number; // 0-100
}

export interface TripSection {
  title?: string;
  subtitle?: string;
  description?: string;
  images: { url: string; caption: string }[];
}

export interface TripPOI {
  uuid?: string;
  name: string;
  description?: string;
  website?: string;
  icon: string; // maki icon name (e.g., 'restaurant', 'museum')
  coords: GeoCoords;
}

export interface TripLocation {
  name: string;
  sections: TripSection[];
  clusters: (QuestMetadata | Obelisk)[];
  poi: TripPOI[];
  paths: Pathway[];
}

export interface TripConfig {
  titleImageUrl?: string;
  poi: any[];
  paths: Pathway[];
  locations: TripLocation[];
  stats?: TripStats;
}

export interface Compilation {
  id?: number;
  name: string;
  description?: string;
  isPublic: boolean;
  onlyForEvents?: boolean;
  questsCount?: number;
  completedQuestsCount?: number;
  obelisksCount?: number;
  visitedObelisksCount?: number;
  location?: GeoCoords;
  activityTags?: string[];
  progress?: number;
  type?: CompilationType;
  quests?: QuestMetadata[];
  obelisks?: Obelisk[];
  tripConfig?: TripConfig;
  creator?: User;
  isFavorite?: boolean;
}

// Trip Generation related interfaces
export interface TripGenerationLocation {
  locationName: string;
  clusters: Array<Array<TripGeneratedPlace | Obelisk | QuestMetadata>>;
}

export interface TripGenerationResult {
  uuid: string;
  userId?: number;
  prompt: string;
  progress?: string;
  generationResults?: {
    data: TripGenerationLocation[];
  };
  generatedCompilation?: Compilation;
  generatedCompilationId?: number;
  errorCode: string;
  errorMessage: string;
  errorDetails: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface TripGeneratedPlace {
  id: number;
  name?: string;
  description?: string;
  lng: number;
  lat: number;
  type: string;
  tags: {
    amenity?: string;
    natural?: string;
    [key: string]: any;
  }
}

// Type guards
export const isTripGeneratedPlace = (obj: any): obj is TripGeneratedPlace => {
  return obj && typeof obj.id === 'number' && typeof obj.lng === 'number' && typeof obj.lat === 'number' && typeof obj.type === 'string';
}

export const isObelisk = (obj: any): obj is Obelisk => {
  return obj && typeof obj.uuid === 'string' && typeof obj.location === 'object';
}

export const isQuestMetadata = (obj: any): obj is QuestMetadata => {
  return obj && typeof obj.id === 'number' && typeof obj.info === 'object' && Array.isArray(obj.steps);
}

